import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { Popover, Paper, Box, Typography, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import { koKR } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

const DEFAULT_FORMAT = 'YYYY-MM-DD HH:mm';

/**
 * 그리드용 날짜+시간 셀 에디터 (yyyy-mm-dd hh:mm)
 * - 왼쪽: 달력(날짜 선택), 오른쪽: 시간 선택. 한 화면에서 날짜·시간 모두 선택 후 확인.
 */
const DateTimeCellEditor = forwardRef((props, ref) => {
  const { stopEditing } = props;
  const anchorRef = useRef(null);
  const currentValueRef = useRef(
    props.value != null && props.value !== '' ? props.value : ''
  );
  const propsRef = useRef(props);
  propsRef.current = props;

  const format =
    props.format ?? props.colDef?.cellEditorParams?.format ?? DEFAULT_FORMAT;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dateValue, setDateValue] = useState(() => {
    const v = props.value;
    if (v != null && v !== '' && dayjs(v).isValid()) return dayjs(v);
    return dayjs();
  });

  const openPopover = () => {
    const v = currentValueRef.current || props.value;
    if (v != null && v !== '' && dayjs(v).isValid()) {
      setDateValue(dayjs(v));
    } else {
      setDateValue(dayjs());
    }
    setPopoverOpen(true);
  };

  useEffect(() => {
    const t = requestAnimationFrame(() => openPopover());
    return () => cancelAnimationFrame(t);
  }, []);

  const handleAnchorInteraction = (e) => {
    e.stopPropagation();
    e.preventDefault();
    openPopover();
  };

  useImperativeHandle(ref, () => ({
    getValue: () => currentValueRef.current ?? '',
  }));

  const getColumnField = () => {
    const col = propsRef.current.column;
    if (col?.getColDef?.()?.field) return col.getColDef().field;
    if (propsRef.current.colDef?.field) return propsRef.current.colDef.field;
    if (col?.getColId?.()) return col.getColId();
    return null;
  };

  const handleDateChange = (date) => {
    if (date && dayjs(date).isValid()) {
      const d = dayjs(date);
      const merged = dateValue
        ? d
            .hour(dateValue.hour())
            .minute(dateValue.minute())
            .second(0)
            .millisecond(0)
        : d;
      const formatted = merged.format(format);
      currentValueRef.current = formatted;
      setDateValue(merged);
    }
  };

  const handleTimeChange = (time) => {
    if (time && dayjs(time).isValid()) {
      const t = dayjs(time);
      const merged = dateValue
        ? dateValue.hour(t.hour()).minute(t.minute()).second(0).millisecond(0)
        : t;
      const formatted = merged.format(format);
      currentValueRef.current = formatted;
      setDateValue(merged);
    }
  };

  const syncPickerValueToRef = () => {
    if (dateValue && dayjs(dateValue).isValid()) {
      currentValueRef.current = dayjs(dateValue).format(format);
    }
  };

  const handleAccept = () => {
    syncPickerValueToRef();
    setPopoverOpen(false);
    stopEditing?.(false);
  };

  const handleCancel = () => {
    setPopoverOpen(false);
    stopEditing?.(true);
  };

  const handlePopoverClose = () => {
    syncPickerValueToRef();
    setPopoverOpen(false);
    stopEditing?.(false);
  };

  const displayText = currentValueRef.current || '날짜 선택';
  return (
    <>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 32,
        }}
      >
        <div
          ref={anchorRef}
          role="button"
          tabIndex={0}
          onMouseDownCapture={handleAnchorInteraction}
          onClick={handleAnchorInteraction}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPopover();
            }
          }}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 8,
            cursor: 'pointer',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        >
          {displayText}
        </div>
      </div>
      {popoverOpen &&
        createPortal(
          <>
            <Popover
              open={popoverOpen}
              anchorEl={anchorRef.current}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              disableScrollLock
              slotProps={{ root: { sx: { zIndex: 10000 } } }}
              PaperProps={{ sx: { zIndex: 10001 } }}
              ModalProps={{ disableScrollLock: true, sx: { zIndex: 10000 } }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <Paper elevation={8} sx={{ overflow: 'hidden' }}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="ko"
                  localeText={
                    koKR?.components?.MuiLocalizationProvider?.defaultProps
                      ?.localeText
                  }
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      minWidth: 520,
                    }}
                  >
                    {/* 왼쪽: 날짜 달력 */}
                    <Box sx={{ borderRight: 1, borderColor: 'divider', p: 1 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ px: 0.5, mb: 0.5 }}
                      >
                        날짜
                      </Typography>
                      <DateCalendar
                        value={dateValue}
                        onChange={handleDateChange}
                        disableFuture={false}
                        sx={{ height: 320, width: 280 }}
                      />
                    </Box>
                    {/* 오른쪽: 시간 선택 */}
                    <Box sx={{ p: 1, minWidth: 200 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ px: 0.5, mb: 0.5 }}
                      >
                        시간
                      </Typography>
                      <MultiSectionDigitalClock
                        value={dateValue}
                        onChange={handleTimeChange}
                        views={['hours', 'minutes']}
                        timeSteps={{ hours: 1, minutes: 5 }}
                        ampm={false}
                        sx={{ maxHeight: 320 }}
                      />
                    </Box>
                  </Box>
                  {/* 하단: 오늘 / 확인 / 취소 */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      p: 1,
                      borderTop: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => {
                        setDateValue(dayjs());
                        currentValueRef.current = dayjs().format(format);
                      }}
                    >
                      오늘
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCancel}
                    >
                      취소
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleAccept}
                    >
                      확인
                    </Button>
                  </Box>
                </LocalizationProvider>
              </Paper>
            </Popover>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
              onMouseDown={handlePopoverClose}
              onClick={handlePopoverClose}
            />
          </>,
          document.body
        )}
    </>
  );
});

DateTimeCellEditor.displayName = 'DateTimeCellEditor';

export default DateTimeCellEditor;
